import { Injectable } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';

import IAppointment from '../../_interfaces/IAppointment';
import ICalendar from '../../_interfaces/ICalendar';
import IPatientHasTreatment from '../../_interfaces/IPatientHasTreatment';
import IPatient from '../../_interfaces/IPatients';
import ISite from '../../_interfaces/ISite';
import { AppointmentService } from './appointment.service';

export interface AppointmentCreationDraft {
  calendar: ICalendar;
  start: Date;
  end: Date;
  patient?: IPatient | null;
  title?: string;
  notes?: string;
  price?: number;
  treatments?: IPatientHasTreatment[];
  sendEmailReminder?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AppointmentCreationService {
  constructor(private readonly appointmentService: AppointmentService) {}

  buildAppointmentName(params: {
    start: Date;
    patient?: IPatient | null;
    title?: string;
  }): string {
    const customTitle = `${params.title || ''}`.trim();
    if (customTitle) {
      return customTitle;
    }

    if (params.patient?.name) {
      return `${params.patient.name} ${params.patient.surname || ''}`.trim();
    }

    return `Appointment ${moment(params.start).format('DD-MM HH:mm')}`;
  }

  buildAppointment(draft: AppointmentCreationDraft): IAppointment | null {
    const calendarId = Number(draft.calendar?.id || 0);
    if (!calendarId || !draft.start || !draft.end) {
      return null;
    }

    return {
      start: moment(draft.start).set('seconds', 0).utc().toDate(),
      end: moment(draft.end).set('seconds', 0).utc().toDate(),
      name: this.buildAppointmentName({
        start: draft.start,
        patient: draft.patient,
        title: draft.title,
      }),
      price: Number(draft.price || 0),
      calendars_id: calendarId,
      description: `${draft.notes || ''}`.trim(),
    };
  }

  buildConflictRange(start: Date, end: Date): { start: string; end: string } {
    return {
      start: moment(start).set('seconds', 0).utc().format('DD-MM-YYYY HH:mm:ss'),
      end: moment(end).set('seconds', 0).utc().format('DD-MM-YYYY HH:mm:ss'),
    };
  }

  findConflicts(
    customersId: number,
    site: ISite,
    calendar: ICalendar,
    start: Date,
    end: Date,
  ): Observable<IAppointment[]> {
    return this.appointmentService.findConfilcts(
      customersId,
      site,
      calendar,
      this.buildConflictRange(start, end),
    );
  }

  registerDraft(
    customersId: number,
    site: ISite,
    draft: AppointmentCreationDraft,
  ): Observable<unknown> {
    const appointment = this.buildAppointment(draft);
    if (!appointment) {
      throw new Error('Unable to build appointment draft');
    }

    return this.appointmentService.registerAppointment(
      customersId,
      site,
      appointment,
      draft.patient as IPatient,
      draft.treatments || [],
      {
        sendEmailReminder: draft.sendEmailReminder === true,
      },
    );
  }
}
